import * as React from 'react';

import {
  Button,
  TextField,
  TextArea,
  LocalizedDate,
  cn,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCopy,
  LucideEye,
  LucideEyeOff,
  LucideCheck,
  LucideRefreshCw,
} from 'lucide-react';

import { webhookRegenerateSecretAtom } from '../../_graphql/mutationWebhookRegenerateSecret';
import {
  $automationName,
  $automationDescription,
  $setAutomationName,
  $setAutomationDescription,
  $isCreateMode,
  $remoteEditorAutomation,
} from '../../_state/automationEditor.state';
import { useTranslations } from '../../i18n/useTranslations';
import { RegenerateSecretDialog } from '../dialogs/RegenerateSecretDialog';

import { runtimeConfig } from '~/config/runtime';

const $regenerateSecret = webhookRegenerateSecretAtom({});

type AutomationDetailsPanelProps = {
  className?: string;
};

export const AutomationDetailsPanel: React.FC<AutomationDetailsPanelProps> = ({
  className,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const isCreateMode = useAtomValue($isCreateMode);
  const automationQuery = useAtomValue($remoteEditorAutomation);
  const automation = automationQuery.data;

  const name = useAtomValue($automationName);
  const description = useAtomValue($automationDescription);
  const setName = useSetAtom($setAutomationName);
  const setDescription = useSetAtom($setAutomationDescription);
  const regenerateSecret = useSetAtom($regenerateSecret);

  const [showSecret, setShowSecret] = React.useState(false);
  const [copiedField, setCopiedField] = React.useState<string | null>(null);
  const [showRegenerateDialog, setShowRegenerateDialog] = React.useState(false);
  const [isRegenerating, setIsRegenerating] = React.useState(false);
  const [overrideSecret, setOverrideSecret] = React.useState<string | null>(
    null,
  );

  const triggerType = automation?.triggerType;
  const webhookEndpoint = automation?.webhookEndpoint;

  const handleCopy = React.useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleRegenerateSecret = React.useCallback(async () => {
    if (!automation?.id) return;
    setIsRegenerating(true);
    try {
      const result = await regenerateSecret({ automationId: automation.id });
      const newSecret = result?.automationsWebhookRegenerateSecret?.secret;
      if (newSecret) {
        setOverrideSecret(newSecret);
        setShowSecret(true);
      }
      toast.success(t.dialogs.regenerateSecret.success);
      setShowRegenerateDialog(false);
    } catch (err) {
      toast.error(
        `${t.dialogs.regenerateSecret.error}: ${err instanceof Error ? err.message : ''}`,
      );
    } finally {
      setIsRegenerating(false);
    }
  }, [automation?.id, regenerateSecret, toast, t]);

  const displayedSecret = overrideSecret ?? webhookEndpoint?.secret;

  const webhookUrl = webhookEndpoint
    ? `${runtimeConfig.api.url}/webhooks/${webhookEndpoint.path}`
    : null;

  return (
    <div
      className={cn(
        '@AutomationDetailsPanel',
        'flex h-full w-96 flex-col border-l border-neutral/10 bg-neutral/5',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-neutral/10 p-4">
        <h3 className="font-semibold">{t.editor.details.title}</h3>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <div>
          <TextField
            label={t.editor.details.name}
            value={name}
            onChange={setName}
            placeholder={
              isCreateMode
                ? t.editor.create.namePlaceholder
                : t.editor.details.namePlaceholder
            }
          />
        </div>

        <div>
          <TextArea
            label={t.editor.details.description}
            value={description}
            onChange={setDescription}
            placeholder={
              isCreateMode
                ? t.editor.create.descriptionPlaceholder
                : t.editor.details.descriptionPlaceholder
            }
            rows={3}
          />
        </div>

        {triggerType && (
          <div className="border-t border-neutral/10 pt-4">
            <label className="mb-1 block text-sm font-medium">
              {t.editor.details.triggerType}
            </label>
            <div className="text-sm text-neutral/60">
              {t.triggerType[triggerType as keyof typeof t.triggerType] ||
                triggerType}
            </div>
          </div>
        )}

        {automation?.status && (
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t.editor.details.status}
            </label>
            <div className="text-sm text-neutral/60">
              {t.status[automation.status as keyof typeof t.status] ||
                automation.status}
            </div>
          </div>
        )}

        {triggerType === 'HttpWebhook' && webhookEndpoint && (
          <div className="border-t border-neutral/10 pt-4">
            <h4 className="mb-3 text-sm font-semibold">
              {t.editor.details.webhookEndpoint}
            </h4>

            <div className="mb-3">
              <label className="mb-1 block text-xs font-medium text-neutral/60">
                {t.editor.details.url}
              </label>
              <div className="flex items-start gap-2">
                <code className="flex-1 rounded bg-neutral/10 px-2 py-1.5 text-xs break-all">
                  {webhookUrl}
                </code>
                <Button
                  variant="ghost"
                  size="small"
                  icon={
                    copiedField === 'url' ? (
                      <LucideCheck className="size-4 text-success" />
                    ) : (
                      <LucideCopy className="size-4" />
                    )
                  }
                  onPress={() => webhookUrl && handleCopy(webhookUrl, 'url')}
                  aria-label={t.editor.details.copyUrl}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="mb-1 block text-xs font-medium text-neutral/60">
                {t.editor.details.secret}
              </label>
              <div className="flex items-start gap-2">
                <code className="flex-1 rounded bg-neutral/10 px-2 py-1.5 font-mono text-xs break-all">
                  {showSecret
                    ? displayedSecret
                    : '••••••••••••••••••••••••••••••••'}
                </code>
                <Button
                  variant="ghost"
                  size="small"
                  icon={
                    showSecret ? (
                      <LucideEyeOff className="size-4" />
                    ) : (
                      <LucideEye className="size-4" />
                    )
                  }
                  onPress={() => setShowSecret(!showSecret)}
                  aria-label={
                    showSecret
                      ? t.editor.details.hideSecret
                      : t.editor.details.showSecret
                  }
                />
                <Button
                  variant="ghost"
                  size="small"
                  icon={
                    copiedField === 'secret' ? (
                      <LucideCheck className="size-4 text-success" />
                    ) : (
                      <LucideCopy className="size-4" />
                    )
                  }
                  onPress={() =>
                    displayedSecret && handleCopy(displayedSecret, 'secret')
                  }
                  aria-label={t.editor.details.copySecret}
                />
              </div>
              <Button
                variant="ghost"
                size="small"
                icon={<LucideRefreshCw className="size-3.5" />}
                onPress={() => setShowRegenerateDialog(true)}
                className="mt-2"
              >
                {t.detail.actions.regenerateSecret}
              </Button>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-neutral/60">
                {t.editor.details.lastUsed}
              </label>
              <div className="text-sm text-neutral/60">
                {webhookEndpoint.lastUsedAt ? (
                  <LocalizedDate date={webhookEndpoint.lastUsedAt} />
                ) : (
                  t.editor.details.never
                )}
              </div>
            </div>
          </div>
        )}

        {isCreateMode && !webhookEndpoint && (
          <div className="border-t border-neutral/10 pt-4">
            <p className="text-xs text-neutral/50">
              {t.editor.details.webhookGenerateHint}
            </p>
          </div>
        )}
      </div>

      <RegenerateSecretDialog
        isOpen={showRegenerateDialog}
        onOpenChange={setShowRegenerateDialog}
        onConfirm={handleRegenerateSecret}
        isRegenerating={isRegenerating}
      />
    </div>
  );
};
